import { CaretDown } from "@phosphor-icons/react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { kebabCase } from "es-toolkit/string"
import { cn } from "@/lib/utils"

export type TAccordionItem<T> = {
  title: string
  content: T
  disabled?: boolean
}

interface CustomAccordionProps<T> {
  items: TAccordionItem<T>[]
  testId?: string
  type?: "single" | "multiple"
}

type AccordionProps = React.ComponentPropsWithRef<typeof AccordionPrimitive.Root>
export const Accordion = ({ className, ref, ...props }: AccordionProps) => (
  <AccordionPrimitive.Root
    ref={ref}
    className={cn("divide-border border-border divide-y overflow-hidden rounded-lg border", className)}
    {...props}
  />
)

type AccordionItemProps = React.ComponentPropsWithRef<typeof AccordionPrimitive.Item>
export const AccordionItem = ({ className, ref, ...props }: AccordionItemProps) => (
  <AccordionPrimitive.Item ref={ref} className={cn("", className)} {...props} />
)

type AccordionTriggerProps = React.ComponentPropsWithRef<typeof AccordionPrimitive.Trigger>
export const AccordionTrigger = ({ className, children, ref, ...props }: AccordionTriggerProps) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between border-inherit px-4 py-2 text-left text-sm leading-5 font-medium outline-0 transition-all",
        "data-disabled:text-on-surface-disabled data-disabled:cursor-not-allowed data-disabled:hover:bg-transparent",
        "hover:bg-surface-hovered focus-visible:bg-surface-focused active:bg-surface-focused [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}>
      {children}
      <CaretDown className="text-on-surface-subdued shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
)

type AccordionContentProps = React.ComponentPropsWithRef<typeof AccordionPrimitive.Content>
export const AccordionContent = ({ className, children, ref, ...props }: AccordionContentProps) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm transition-all"
    {...props}>
    <div className={cn("px-4 pt-2 pb-4", className)}>{children}</div>
  </AccordionPrimitive.Content>
)

export const CustomAccordion = <T extends React.ReactNode>({
  items,
  testId,
  type = "single",
}: CustomAccordionProps<T>) => (
  <Accordion data-testid={testId} collapsible type={type}>
    {items.map((item, index) => (
      <AccordionItem
        key={kebabCase(item.title)}
        value={`item-${index}`}
        data-testid={`accordion-item-${index}`}
        disabled={item.disabled}>
        <AccordionTrigger data-testid={`accordion-trigger-${index}`}>{item.title}</AccordionTrigger>
        <AccordionContent data-testid={`accordion-content-${index}`}>{item.content}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
)
